import { Injectable, Injector } from '@angular/core';
import { CalendarEvent, CalendarView } from 'angular-calendar';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { ApiService } from 'src/app/api.service';
import * as moment from 'moment-timezone';
import { ModalController, NavController } from '@ionic/angular';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { UsuarioServiceProvider } from '../index.providers';
import { CourseDetailsModalComponent } from 'src/app/components/courses-components/course-details-modal/course-details-modal.component';
import { ChecklistDetailsComponent } from 'src/app/components/calendar-events/checklist-details/checklist-details.component';
import { AppPlatformService } from '../platform/app-platform.service';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { RewardsService } from '../rewards/rewards.service';
import { ToastService } from '../toast-service/toast.service';
import { Browser } from '@capacitor/browser';
import { environment } from 'src/environments/environment';
import { LoginMultiModuloService } from '../login-multi-modulo/login-multi-modulo.service';

interface CustomCalendarEvent extends CalendarEvent {
  custom?: {
    eventName?: string;
    eventTimeRange?: string;
    type?: {label: string, code: string};
    id?: number;
    schoolId?: number,
    timezone?: string;
  };
}


@Injectable({
  providedIn: 'root'
})
export class CalendarService {
  private calendarType = new BehaviorSubject<CalendarView>(CalendarView.Week);
  calendarType$ = this.calendarType.asObservable();

  private _calendarEvents = new BehaviorSubject<CustomCalendarEvent[]>([]);
  calendarEvents$ = this._calendarEvents.asObservable();

  // --- Dynamic range-based loading state ---
  private loadedMonths = new Set<string>();
  private allCachedEvents: CustomCalendarEvent[] = [];
  private pendingLoads = new Set<string>();
  private activeLoadingCount = 0;
  private _isLoadingEvents = new BehaviorSubject<boolean>(false);
  isLoadingEvents$ = this._isLoadingEvents.asObservable();
  private syncedEventKeys = new Set<string>();
  private calendarIsAvailable = new BehaviorSubject<boolean>(false);
  calendarIsAvailable$ = this.calendarIsAvailable.asObservable();
  baseImgUrl: string = '';
  rewardsIsActive: boolean = false;
  rewardSub: Subscription;
  coinCustomImage: string = '';

  constructor(
    private api: ApiService,
    private navCtrl: NavController,
    public router: Router,
    private up: UsuarioServiceProvider,
    public modalCtrl: ModalController,
    private appPlatformService:AppPlatformService,
    private iab: InAppBrowser,
    private rewardsService: RewardsService,
    private toastService: ToastService,
    private injector: Injector,
  ) {
    this.up.isLogged.subscribe((isLogged) => {
      if (!isLogged) {
        this.clearCache();
      }
    });
  }

  public setCalendarType(type: CalendarView) {
    this.calendarType.next(type);
  }

  public setCalendarIsAvailable(isAvailable: boolean) {
    this.calendarIsAvailable.next(isAvailable);
  }

  /**
   * Initial load: fetches events for the current month + next month (2-month range).
   */
  getCalendarEvents() {
    const tz = moment.tz.guess();
    const now = moment.tz(tz);
    const month1Key = now.format('YYYY-MM');
    const month2Key = now.clone().add(1, 'month').format('YYYY-MM');

    if (this.loadedMonths.has(month1Key) && this.loadedMonths.has(month2Key)) {
      return;
    }

    const starts_at = now.clone().startOf('month').format('YYYY-MM-DD');
    const finishes_at = now.clone().add(1, 'month').endOf('month').format('YYYY-MM-DD');

    this.loadRange(starts_at, finishes_at, [month1Key, month2Key], true);
  }

  /**
   * Called when the user navigates to a new date.
   * Ensures events for that date's range are loaded and prefetches adjacent ranges.
   */
  ensureEventsLoaded(viewDate: Date): void {
    const tz = moment.tz.guess();
    const m = moment.tz(viewDate, tz);
    const month1Key = m.format('YYYY-MM');
    const month2Key = m.clone().add(1, 'month').format('YYYY-MM');

    const monthsToLoad: string[] = [];
    if (!this.loadedMonths.has(month1Key)) monthsToLoad.push(month1Key);
    if (!this.loadedMonths.has(month2Key)) monthsToLoad.push(month2Key);

    if (monthsToLoad.length > 0) {
      const rangeStart = moment.tz(monthsToLoad[0] + '-01', tz).startOf('month').format('YYYY-MM-DD');
      const rangeEnd = moment.tz(monthsToLoad[monthsToLoad.length - 1] + '-01', tz).endOf('month').format('YYYY-MM-DD');
      this.loadRange(rangeStart, rangeEnd, monthsToLoad, true);
    }

    // Prefetch adjacent ranges (silent, no loading indicator)
    this.prefetch(viewDate);
  }

  /**
   * Clears the in-memory cache. Call when leaving the calendar view.
   */
  clearCache(): void {
    this.loadedMonths.clear();
    this.allCachedEvents = [];
    this.pendingLoads.clear();
    this.activeLoadingCount = 0;
    this._calendarEvents.next([]);
    this._isLoadingEvents.next(false);
  }

  private prefetch(viewDate: Date): void {
    const tz = moment.tz.guess();
    const m = moment.tz(viewDate, tz);

    // Forward: next 2 months beyond the current range
    const fwd1Key = m.clone().add(2, 'months').format('YYYY-MM');
    const fwd2Key = m.clone().add(3, 'months').format('YYYY-MM');
    const fwdToLoad: string[] = [];
    if (!this.loadedMonths.has(fwd1Key)) fwdToLoad.push(fwd1Key);
    if (!this.loadedMonths.has(fwd2Key)) fwdToLoad.push(fwd2Key);
    if (fwdToLoad.length > 0) {
      const rangeStart = moment.tz(fwdToLoad[0] + '-01', tz).startOf('month').format('YYYY-MM-DD');
      const rangeEnd = moment.tz(fwdToLoad[fwdToLoad.length - 1] + '-01', tz).endOf('month').format('YYYY-MM-DD');
      this.loadRange(rangeStart, rangeEnd, fwdToLoad, false);
    }

    // Backward: previous 2 months before the current range
    const bwd1Key = m.clone().subtract(2, 'months').format('YYYY-MM');
    const bwd2Key = m.clone().subtract(1, 'month').format('YYYY-MM');
    const bwdToLoad: string[] = [];
    if (!this.loadedMonths.has(bwd1Key)) bwdToLoad.push(bwd1Key);
    if (!this.loadedMonths.has(bwd2Key)) bwdToLoad.push(bwd2Key);
    if (bwdToLoad.length > 0) {
      const rangeStart = moment.tz(bwdToLoad[0] + '-01', tz).startOf('month').format('YYYY-MM-DD');
      const rangeEnd = moment.tz(bwdToLoad[bwdToLoad.length - 1] + '-01', tz).endOf('month').format('YYYY-MM-DD');
      this.loadRange(rangeStart, rangeEnd, bwdToLoad, false);
    }
  }

  private loadRange(starts_at: string, finishes_at: string, monthKeys: string[], showLoading: boolean): void {
    const rangeKey = `${starts_at}_${finishes_at}`;
    if (this.pendingLoads.has(rangeKey)) return;
    if (monthKeys.every(k => this.loadedMonths.has(k))) return;

    this.pendingLoads.add(rangeKey);
    if (showLoading) {
      this.activeLoadingCount++;
      this._isLoadingEvents.next(true);
    }

    this.api.getCalendarEvents(starts_at, finishes_at).subscribe({
      next: ({ data }) => {
        const parsed = this.parseEvents(data);
        monthKeys.forEach(k => this.loadedMonths.add(k));
        this.addEventsToCache(parsed);
        this.pendingLoads.delete(rangeKey);
        if (showLoading) {
          this.activeLoadingCount--;
          if (this.activeLoadingCount === 0) {
            this._isLoadingEvents.next(false);
          }
        }
      },
      error: (e) => {
        // console.log(e);
        this.pendingLoads.delete(rangeKey);
        if (showLoading) {
          this.activeLoadingCount--;
          if (this.activeLoadingCount === 0) {
            this._isLoadingEvents.next(false);
          }
        }
      }
    });
  }

  private parseEvents(data: any[]): CustomCalendarEvent[] {
    return data.map((event: any) => {
      const tz = event.custom?.timezone || moment.tz.guess();
      return {
        ...event,
        start: moment.tz(event.start, tz).toDate(),
        end: moment.tz(event.end, tz).toDate(),
        custom: {
          ...event.custom,
          id: event.custom?.type.code === 'topic' ? event.custom.topic_id : event.custom.id,
        }
      };
    });
  }

  private getEventKey(event: CustomCalendarEvent): string {
    // console.log("getEventKey", event.start.getTime());
    // if (event.id != null) return String(event.id);
    if (event.custom?.id != null && event.custom?.type?.code) {
      // there are events that share the same custom.id and type.code but are diferent in start/end time, so we need to include those in the key to avoid collisions in the cache
      return `${event.custom.type.code}_${event.custom.id}_${event.start.getTime()}_${event.end?.getTime()}`;
    }
    return `${event.start.getTime()}_${event.end?.getTime()}_${event.title}`;
  }

  private addEventsToCache(newEvents: CustomCalendarEvent[]): void {
    const existingKeys = new Set(this.allCachedEvents.map(e => this.getEventKey(e)));
    const uniqueNewEvents = newEvents.filter(e => !existingKeys.has(this.getEventKey(e)));
    uniqueNewEvents.forEach((e: any) => {
      if (this.syncedEventKeys.has(this.getSyncKey(e))) e.synced = true;
    });
    this.allCachedEvents = [...this.allCachedEvents, ...uniqueNewEvents];
    this._calendarEvents.next(this.allCachedEvents);
  }

  /** Tipo, id y comienzo: lo que identifica un evento tanto en la caché como en una copia. */
  private getSyncKey(event: any): string {
    return `${event?.custom?.type?.code}_${event?.custom?.id}_${new Date(event?.start).getTime()}`;
  }

  /**
   * Marca como vinculado el evento de la caché, no la copia que viajó al API
   * (`{...eventoClicked, path}`, o lo que vuelve de localStorage después de Google): sin
   * esto el modal seguía ofreciendo «Añadir a mi calendario» hasta recargar. La clave se
   * recuerda porque, al volver de Google, los eventos se piden a la vez que se crea el de
   * Google y pueden llegar con `synced: false`.
   */
  private markEventSynced(event: any): void {
    const key = this.getSyncKey(event);
    this.syncedEventKeys.add(key);
    let changed = false;
    this.allCachedEvents.forEach((cached: any) => {
      if (this.getSyncKey(cached) === key) {
        cached.synced = true;
        changed = true;
      }
    });
    if (changed) {
      this._calendarEvents.next([...this.allCachedEvents]);
    }
  }

  redirectToEvent(typeEvent, eventApiData) {
    // console.log("eventApiData:: ", eventApiData)
    if(typeEvent === 'course') {
      const course = eventApiData;
      course.is_rewards_exchanged ?
        this.goToCoursesAsBenefits(course.school_id, course,course.ultimo_tema_visto)
        :
        this.irAlTema( course.modality_code && (course.modality_code === 'in-person' || course.modality_code === 'virtual')
          ? course : null,
          course.school_id,
          course.id,
          course.ultimo_tema_visto || null,
          course.format
        );
    };
    if(typeEvent === 'checklist') {
      const checklist = eventApiData;

      if(checklist.auditor_calificate_all_entity) {
			  this.router.navigate([`lista-entidades/${checklist.id}`]);
      }
      else {
        if(checklist.modality.code == 'qualify_user')	{
          let extras: NavigationExtras = {
            queryParams: {
              filter: 'realizado',
            },
          };
          this.router.navigate([`lista-asignados/${checklist.id}`],extras);
        }	else {
          let extras: NavigationExtras = {
            queryParams: {
              filter: 'realizado',
            },
          };
          this.router.navigate([`checklist-actividades/${checklist.id}`],extras);
        }
      };
    };

     if(typeEvent === 'benefit') {
        const benefit = eventApiData;
        this.goToBenefit(benefit.id);
     }
     if(typeEvent === 'live_session') {
        const liveSession = eventApiData;
        this.goToLiveSession();
     }
  }

  getRouteToRedirect(typeEvent: string, eventApiData: any): string | null {
    const path = this.getPathToRedirect(typeEvent, eventApiData);
    if(path) {
      if(environment.APP_MULTIMARCA) {
        return `${this.getMultibrandBaseUrl()}${path}`;
      } else {
        return `${environment.URL_BASE}${path}`;
      }
    }
    return path;
  }

  /**
   * Base (con `/` final) del link de integración en multimarca. Capas, la primera
   * que dé valor: dominio propio de la marca donde está el usuario, `app_url` de la
   * marca guardado en `api_urls` y, por último, la app genérica.
   */
  private getMultibrandBaseUrl(): string {
    // Se resuelve al usarse, no en el constructor: LoginMultiModuloService depende de
    // FuncionesServiceProvider, que depende de este servicio (NG0200 si se inyecta directo).
    const loginMultiModuloService = this.injector.get(LoginMultiModuloService);
    if (loginMultiModuloService.isCustomBrandDomain()) {
      return `${window.location.origin}/`;
    }
    const brandBase = loginMultiModuloService.parseBaseUrl(this.api.getApiUrls?.urlBase);
    return brandBase || 'https://app.cursalab.io/';
  }

  getPathToRedirect(typeEvent: string, eventApiData: any): string | null {
    if (typeEvent === 'course') {
      const course = eventApiData;
      if (course.is_rewards_exchanged) {
        if (!course.disponible) return null;
        const topic = course.ultimo_tema_visto;
        if (topic?.id && typeof topic.id === 'number') {
          return `tema-beneficio/escuela/${course.school_id}/cursos/${course.id}/tema/${topic.id}`;
        }
        return `tema-beneficio/escuela/${course.school_id}/cursos/${course.id}/tema/`;
      } else {
        const isInPersonOrVirtual = course.modality_code && (course.modality_code === 'in-person' || course.modality_code === 'virtual');
        if (isInPersonOrVirtual) {
          const topicId = course.eventoClickedData?.custom?.topic_id;
          if (topicId) {
            return `lista-reuniones/opciones-curso/${topicId}`;
          }
          return `lista-reuniones`;
        }
        if (course.format === 'path') {
          return `escuela/${course.school_id}/cursos/${course.id}/ruta`;
        }
        return `escuela/${course.school_id}/cursos/${course.id}/tema/${course.ultimo_tema_visto || null}`;
      }
    }

    if (typeEvent === 'checklist') {
      const checklist = eventApiData;
      if (checklist.auditor_calificate_all_entity) {
        return `lista-entidades/${checklist.id}`;
      }
      if (checklist.modality.code === 'qualify_user') {
        return `lista-asignados/${checklist.id}?filter=realizado`;
      }
      return `checklist-actividades/${checklist.id}?filter=realizado`;
    }

    if (typeEvent === 'benefit') {
      return `beneficio?beneficio=${JSON.stringify(eventApiData.id)}`;
    }

    if (typeEvent === 'live_session') {
      return `lista-reuniones`;
    }

    return null;
  }

  irAlTema(courseInPersonData, escuela, curso, tema, format?: string) {
    if (
      courseInPersonData &&
      (courseInPersonData.modality_code === 'in-person' ||
        courseInPersonData.modality_code === 'virtual')
    ) {
      if(courseInPersonData?.eventoClickedData?.custom?.topic_id) {
        this.navCtrl.navigateRoot([`lista-reuniones/opciones-curso/${courseInPersonData.eventoClickedData?.custom?.topic_id}`]);
      } else {
        let navigationExtras: NavigationExtras = {
          queryParams: {
            from: 'cursos',
            status: courseInPersonData.status,
          },
        };
        this.navCtrl.navigateRoot(['lista-reuniones'], navigationExtras);
      }
    } else if (format === 'path') {
      // Mismo criterio que en el listado de cursos: un curso en ruta abre su propia pantalla.
      this.navCtrl.navigateForward(`escuela/${escuela}/cursos/${curso}/ruta`);
    } else {
      this.navCtrl.navigateForward(
        `escuela/${escuela}/cursos/${curso}/tema/${tema}`
      );
    }
  }

  goToCoursesAsBenefits(school, course, topic, fromRecents = false) {
    // console.log("goToCoursesAsBenefits", school, course, topic, fromRecents)
    if (!course.disponible && !fromRecents) return;
    if (topic?.id && typeof topic.id === 'number') {
      this.navCtrl.navigateForward(
        `tema-beneficio/escuela/${school}/cursos/${course.id}/tema/${topic.id}`
      );
    } else {
      this.navCtrl.navigateForward(
        `tema-beneficio/escuela/${school}/cursos/${course.id}/tema/`
      );
    }
  }

  goToBenefit(benefitID){
    let extras: NavigationExtras = {
      queryParams: {
        beneficio: JSON.stringify(benefitID),
      },
    };
    this.router.navigate(['beneficio'], extras);
  }

  goToLiveSession(){
    this.router.navigate(['lista-reuniones']);
  }

  public onEventoClick(event) {
    const eventoClickeado = event.event;
    // console.log('Evento individual:', eventoClickeado);

    switch (eventoClickeado.custom?.type?.code) {
      case "course":
        this.displayCourseDetailsWithLoading(eventoClickeado);
        break;
      case "topic":
        this.displayCourseDetailsWithLoading(eventoClickeado);
        break;
      case "checklist":
        this.displayChecklistDetails(eventoClickeado);
        break;
      case "benefit":
        this.displayBenefitDetails(eventoClickeado);
        break;
      case "live_session":
        this.displayLiveSessionDetails(eventoClickeado);
        break;
      default:
        break;
    }
  }

  async displayCourseDetailsWithLoading(eventoClicked) {
    const courseDataSubject = new Subject<any>();
    let courseRef = null;

    const modalAvailable = await this.modalCtrl.create({
      component: CourseDetailsModalComponent,
      backdropDismiss: true,
      cssClass: 'course-deatils-modal',
      componentProps: {
        loading: true,
        courseData$: courseDataSubject,
      },
    });
    await modalAvailable.present();

    let courseID = null;
    let type = null;
    if(eventoClicked.custom?.type?.code === "course") {
      courseID = eventoClicked.custom?.id;
      type = eventoClicked.custom?.type?.code;
    }
    if(eventoClicked.custom?.type?.code === "topic") {
      courseID = eventoClicked.custom?.course_id;
      type = "course";
    }

    this.baseImgUrl = this.up.getBucketBaseUrl;

    this.api.getCalendarEventData(courseID, type)
      .subscribe({
        next: ({ data: course }) => {
          courseRef = course;
          courseDataSubject.next({
            coverUrl: course.imagen ? this.baseImgUrl + course.imagen : '',
            teaserUrl: course.preview_video_url,
            courseName: course.name,
            percentage: course.porcentaje,
            completedTopics: course.temas_completados,
            allTopics: course.temas_asignados,
            duration: course.duration,
            courseTags: course.tags,
            description: course.descripcion,
            rewardCoins: this.rewardsIsActive && course.awards_coins ? course.coins : null,
            coinCustomImage: this.coinCustomImage,
            courseIsLocked: !course.disponible,
            hasSurvey: course.encuesta,
            hasEvaluation: course.is_qualified,
            hasTasks: course.has_tarea,
            hasChecklist: course.has_checklist,
            topics: course.topics,
            address:  eventoClicked?.custom?.address ? eventoClicked.custom.address : null,
            fromCalendar: true,
            eventIsLocked: this.isEventLocked(eventoClicked.start),
            courseStartDate: eventoClicked.start,
            eventIsSynced: eventoClicked.synced,
          });
        },
        error: (error) => {
          console.error('Error al obtener datos del evento curso:', error);
          modalAvailable.dismiss();
        }
      });

    const { data } = await modalAvailable.onWillDismiss();
    if (data && data === 'available' && courseRef) {
      this.redirectToEvent(type, {...courseRef, eventoClickedData: eventoClicked});
    }
    else if (data && data === 'integrateCalendarEvent') {
      // console.log("click integrate Calendar Event...")
      this.initCalendarIntegration({...eventoClicked, path: this.getRouteToRedirect(type, {...courseRef, eventoClickedData: eventoClicked})});
    } else {
      localStorage.removeItem('cal_event_integration_pending');
    }
  }

  async displayChecklistDetails(eventoClicked) {
     // console.log('display Checklis!!');
      const modalAvailable = await this.modalCtrl.create({
        component: ChecklistDetailsComponent,
        backdropDismiss: true,
        cssClass: 'course-deatils-modal',
        componentProps: {
          eventName: eventoClicked.title,
          eventType: eventoClicked.custom?.type?.code,
          dateEventStart: eventoClicked.start,
          dateEventEnd: eventoClicked.end,
          eventIsLocked: this.isEventLocked(eventoClicked.start),
          eventIsSynced: eventoClicked.synced,
        },
      });
      await modalAvailable.present();
      const { data } = await modalAvailable.onWillDismiss();
      if (data && data === 'available') {
        this.redirectToEvent(eventoClicked?.custom?.type?.code, eventoClicked?.custom);
      } else if (data && data === 'integrateCalendarEvent') {
        // console.log("click integrate Calendar Event...")
        this.initCalendarIntegration({...eventoClicked, path: this.getRouteToRedirect(eventoClicked?.custom?.type?.code, eventoClicked?.custom)});
      } else {
        localStorage.removeItem('cal_event_integration_pending');
      }

  }

  async displayBenefitDetails(eventoClicked) {
      // console.log('display Benefit Details!!');
      const modalAvailable = await this.modalCtrl.create({
        component: ChecklistDetailsComponent,
        backdropDismiss: true,
        cssClass: 'course-deatils-modal',
        componentProps: {
          eventName: eventoClicked.title,
          eventType: eventoClicked.custom?.type?.code,
          dateEventStart: eventoClicked.start,
          dateEventEnd: eventoClicked.end,
          address: eventoClicked.custom?.address,
          eventIsLocked: this.isEventLocked(eventoClicked.start),
          eventIsSynced: eventoClicked.synced,
        },
      });
      await modalAvailable.present();
      const { data } = await modalAvailable.onWillDismiss();
      if (data && data === 'available') {
        this.redirectToEvent(eventoClicked?.custom?.type?.code, {id: eventoClicked?.custom?.id});
      }
      else if (data && data === 'integrateCalendarEvent') {
       // console.log("click integrate Calendar Event...")
        this.initCalendarIntegration({...eventoClicked, path: this.getRouteToRedirect(eventoClicked?.custom?.type?.code, {id: eventoClicked?.custom?.id})});
      } else {
        localStorage.removeItem('cal_event_integration_pending');
      }
  }
  async displayLiveSessionDetails(eventoClicked) {
      // console.log('display liveSession Details!!');
      const modalAvailable = await this.modalCtrl.create({
        component: ChecklistDetailsComponent,
        backdropDismiss: true,
        cssClass: 'course-deatils-modal',
        componentProps: {
          eventName: eventoClicked.title,
          eventType: eventoClicked.custom?.type?.code,
          dateEventStart: eventoClicked.start,
          dateEventEnd: eventoClicked.end,
          eventIsLocked: this.isEventLocked(eventoClicked.start),
          eventIsSynced: eventoClicked.synced,
        },
      });
      await modalAvailable.present();
      const { data } = await modalAvailable.onWillDismiss();
      if (data && data === 'available') {
        this.redirectToEvent(eventoClicked?.custom?.type?.code, eventoClicked?.custom);
      }
      else if (data && data === 'integrateCalendarEvent') {
        // console.log("click integrate Calendar Event...")
        this.initCalendarIntegration({...eventoClicked, path: this.getRouteToRedirect(eventoClicked?.custom?.type?.code, eventoClicked?.custom )});
      } else {
        localStorage.removeItem('cal_event_integration_pending');
      }
  }

  initCalendarIntegration(eventoClicked, oneChanceToRetry = false) {
    const dismissLoadingToast = this.toastService.presentDismissableToast("calendar", "Vinculando evento...", 2000, "top");
    this.api.requestEventIntegration(eventoClicked)
    .subscribe({
      next: async (response) => {
        await (await dismissLoadingToast)();
        // console.log("Event integration api response:", response?.data, response?.data?.redirect_google);
        if(oneChanceToRetry && !response?.data?.event_created) {
          console.log("one Chance To Retry")
          // Sin borrarlo, cada recarga con ?restart_integration=true volvía a mostrar el error.
          localStorage.removeItem('cal_event_integration_pending');
          this.toastService.presentToast("error", "Error al vincular el evento", 4000, "top");
          return;
        }
        if(response?.data?.event_created && !response?.data?.redirect) {
          localStorage.removeItem('cal_event_integration_pending');
          this.toastService.presentToast( "confirm" , "Evento vinculado correctamente a tu calendario", 4000, "top" );
          eventoClicked.synced = true;
          this.markEventSynced(eventoClicked);

        } else if( !response?.data?.event_created && response?.data?.redirect_google) {
          localStorage.setItem('cal_event_integration_pending', JSON.stringify(eventoClicked));

          if(this.appPlatformService.isAppPlatform()) {
            // Escuchar cierre del navegador del sistema como fallback
            const browserListener = await Browser.addListener('browserFinished', () => {
              // console.log('*** Calendar: browserFinished - checking pending integration');
              browserListener.remove();
              //  localStorage.removeItem('cal_event_integration_pending');

              // const pending = localStorage.getItem('cal_event_integration_pending');
              // if (pending) {
              //   this.initCalendarIntegration(JSON.parse(pending), true);
              // }
            });

            // Abrir con navegador seguro del sistema (Chrome Custom Tabs / SFSafariViewController)
            // El backend detecta que es app y redirige al custom scheme, que es capturado
            // por el handler global appUrlOpen en app.component.ts
            await Browser.open({
              url: response?.data?.redirect_google,
              presentationStyle: 'popover'
            });
          } else {
            window.open(response?.data?.redirect_google, "_system");
          }

        }
      },
      error: async (error) => {
        await (await dismissLoadingToast)();
        console.error("Error integrating event:", error);
        localStorage.removeItem('cal_event_integration_pending');
        this.toastService.presentToast("error", "Error al vincular el evento", 4000, "top");
      }
    });
  }

    getRewardData() {
    if (this.rewardsService.getRewardActiveStatus()) {
      this.rewardSub = this.rewardsService.rewardConfigData$.subscribe(
        (rewardData) => {
          this.rewardsIsActive = rewardData.rewardsIsActive;
          this.coinCustomImage = rewardData.coinCustomImage;
        }
      );
    } else {
      this.api.getRewardsConfig().subscribe({
        next: ({ data }) => {
          // console.log(data);
          this.rewardSub = this.rewardsService.rewardConfigData$.subscribe(
            (rewardData) => {
              this.rewardsIsActive = rewardData.rewardsIsActive;
              this.coinCustomImage = rewardData.coinCustomImage;
            }
          );

          const rewardConfig = {
            rewardsIsActive: data.has_rewards,
            currencyName: data.rewards?.coin_name,
            coinCustomImage: data.rewards?.coin_image,
            coinsCustomImage: data.rewards?.coins_image,
            userRankData: data.user_rank,
            howToGetCoins: data.rewards?.how_to_get_coins,
            totalCoins: data.rewards?.coins,
          };
          this.rewardsService.setRewardConfig(rewardConfig);
        },
        error: (e) => {
          console.log(e);
        },
      });
    }
  }

  /**
   * Determina si un evento está bloqueado según su fecha de inicio.
   * Un evento está bloqueado si aún no ha comenzado (fecha de inicio > ahora).
   */
  private isEventLocked(eventStart: Date): boolean {
    return new Date(eventStart).getTime() > Date.now();
  }



}
